import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Palette, Heart, Globe, Award } from 'lucide-react';

const About = () => {
  return (
    <div data-testid="about-page">
      <SEO 
        title="About Us"
        description="Learn about ARTCARDO - A DULCIA.ART initiative empowering independent artists and artisans worldwide. Discover our mission to connect artists with art lovers."
        keywords="about ARTCARDO, art marketplace, DULCIA.ART, support artists, handmade art"
        url="/about"
      />
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="about-title">About ARTCARDO</h1>
          <p className="section-subtitle max-w-3xl mx-auto" data-testid="about-intro">
            ARTCARDO is more than just a marketplace – it's a vibrant community where artists and art lovers come together to celebrate creativity and craftsmanship. ARTCARDO proudly belongs to DULCIA.ART — a purpose-driven ecosystem dedicated to empowering artists and artisans through curated live events, international business consulting, and technology-led platforms, including the online marketplace ARTCARDO and the global music community MYBIGSTAGE.
          </p>

          <div className="grid md:grid-cols-2 gap-12 mt-12">
            <div className="card" data-testid="mission-card">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <Heart className="w-6 h-6 mr-3 text-[#ef4444]" />
                Our Mission
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We believe that every artist deserves a platform to showcase their talent
                and earn a sustainable income from their passion. Our mission is to empower
                independent artists and artisans by connecting them with passionate customers
                who value unique, handcrafted art.
              </p>
            </div>

            <div className="card" data-testid="story-card">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <Palette className="w-6 h-6 mr-3 text-[#ef4444]" />
                Our Story
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Founded in 2025, ARTCARDO was born from a simple idea: to create a space
                where art transcends geographical boundaries. We started with a handful of
                artists and have grown into a thriving community of creators and collectors
                from across India.
              </p>
            </div>
          </div>

          {/* Platform Features Banner */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-8">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">How ARTCARDO Works</h3>
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="bg-white p-4 rounded-lg shadow">
                  <h4 className="font-bold text-blue-900 mb-2">For Artists</h4>
                  <p className="text-sm text-gray-700">
                    • Easy registration<br />
                    • Upload your artworks<br />
                    • Secure payment processing<br />
                    • Deserved price for your art
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <h4 className="font-bold text-blue-900 mb-2">For Buyers</h4>
                  <p className="text-sm text-gray-700">
                    • Browse curated art<br />
                    • Easy Purchase online<br />
                    • Multiple payment options<br />
                    • Direct artist delivery
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <h4 className="font-bold text-blue-900 mb-2">Easy Purchase Process</h4>
                  <p className="text-sm text-gray-700">
                    • Secure online checkout<br />
                    • Payment: Through online<br />
                    • Order tracking system<br />
                    • Safe & encrypted
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-semibold text-center mb-8" data-testid="why-choose-title">Why Choose ARTCARDO?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center" data-testid="benefit-commission">
                <div className="w-16 h-16 bg-[#f5f5f5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-[#ef4444]" />
                </div>
                <h4 className="font-semibold mb-2">Secure Platform</h4>
                <p className="text-sm text-gray-600">
                  Safe & encrypted payment processing. Artists earn the price they deserve for their art.
                </p>
              </div>
              <div className="text-center" data-testid="benefit-quality">
                <div className="w-16 h-16 bg-[#f5f5f5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Palette className="w-8 h-8 text-[#ef4444]" />
                </div>
                <h4 className="font-semibold mb-2">Curated Quality</h4>
                <p className="text-sm text-gray-600">
                  Every artist and product is carefully reviewed to maintain the highest
                  quality standards
                </p>
              </div>
              <div className="text-center" data-testid="benefit-support">
                <div className="w-16 h-16 bg-[#f5f5f5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-[#ef4444]" />
                </div>
                <h4 className="font-semibold mb-2">Global Reach</h4>
                <p className="text-sm text-gray-600">
                  Connect with art lovers worldwide and expand your customer base beyond
                  local boundaries
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-semibold mb-4" data-testid="join-title">Join Our Community</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Whether you're an artist looking to share your work or an art enthusiast
              searching for unique pieces, ARTCARDO welcomes you. Together, we're building
              a future where art and creativity flourish.
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default About;